import { AnySchema } from 'yup';
import React, { Dispatch, SetStateAction } from 'react';
import {
  ExtendedComponentSchema,
  FormConfig,
  PreloadedDataValidationOptions,
} from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { APIGatewayResponseDataHeader } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';

export type ClientSideWizardState = {
  preloadedData: Nullable<GenericObject>;
  formData: GenericObject;
  stepsConfig: { [key: string]: ClientSideWizardStepConfiguration };
  currentProcessState: ClientSideWizardCurrentProcessState;
  setState: Dispatch<SetStateAction<ClientSideWizardCurrentProcessState>>;
  errorState: { errors: Nullable<GenericObject>; setNetworkCallErrors: Function };
  goToNextWizardStep: (formData: any) => void;
  goToPreviousWizardStep: () => void;
  currentStepConfig: Nullable<ClientSideWizardStepConfiguration>;
  hasStepIndicator: boolean;
  totalSteps: number;
  completedSteps: number;
  onCancel?: Function;
  onSubmit?: Function;
  shouldNavigateTo: Nullable<WizardNavigationActionsEnum>;
  initialized: boolean;
};

export type ClientSideWizardStateHandlerProps = {
  state: ClientSideWizardState;
  stepData: GenericObject;
  formData: GenericObject;
  setState: Dispatch<React.SetStateAction<ClientSideWizardCurrentProcessState>>;
  stepsConfig: { [key: string]: ClientSideWizardStepConfiguration };
  currentStepId?: string;
  formDataPreloader: (currentStepConfig: ClientSideWizardStepConfiguration) => ClientSideWizardStepConfiguration;
  completedSteps: number;
};

export type PreloadedFormData = Nullable<GenericObject<Nullable<{ stepId: string; values: GenericObject<any> }>>>;
export interface PreloadedFormDataConfig {
  currentStep?: Nullable<string>;
  previousStep?: Nullable<string>;
  data: PreloadedFormData;
  preloadDataAdapterName?: string;
  validationOptions?: PreloadedDataValidationOptions;
}

export interface ClientSideWizardProviderProps {
  preloadedFormDataConfig?: PreloadedFormDataConfig;
  stepsConfig: { [key: string]: ClientSideWizardStepConfiguration };
  processName: string;
  stateHandler: (
    props: ClientSideWizardStateHandlerProps,
    state: ClientSideWizardState,
    callback: (obj: GenericObject) => void,
  ) => void;
  resumeProcessQueryParam?: string;
  onStepChange?: (completedSteps: number, totalSteps: number) => void;
  onSubmit?: (currentProcessState?: ClientSideWizardCurrentProcessState) => Promise<void> | void | undefined;
  onSuccess?: (currentProcessState?: ClientSideWizardCurrentProcessState) => void | undefined;
  onProcessEnd?: (currentProcessState?: ClientSideWizardCurrentProcessState) => void | undefined;
  onCancel?: React.MouseEventHandler;
  onPreviousFirstStep?: (currentProcessState?: ClientSideWizardCurrentProcessState) => void | undefined;
}

export interface ClientSideWizardCurrentProcessState {
  currentStepConfig: Nullable<ClientSideWizardStepConfiguration>;
  stepId: string | undefined;
  completedSteps: number;
  formData?: GenericObject;
  preloadedFormDataConfig?: PreloadedFormDataConfig;
}

//TODO change is OTP to authorizationMethod with possible values 'OTP', 'SCA'
export interface ClientSideWizardContentsConfig extends FormConfig {
  backButton?: GenericObject;
  nextStepButton?: GenericObject;
  cancelButton?: GenericObject;
  resentButton?: GenericObject;
  resetButton?: GenericObject;
  stepId?: string;
  disableGutters?: boolean;
  fixed?: boolean;
  title?: string;
  formTitleSettings?: FormTitleSettings;
  status?: string;
  isOTP?: boolean;
  description?: string;
  icon?: 'error' | 'activateEmail' | 'faceID' | 'locker' | 'tick' | 'touchID';
  iconError?: 'error';
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  actionsSettings?: WizardActionsSettings;
  actionFeedbackMessages?: APIGatewayResponseDataHeader[];
  hasRedirect?: boolean;
  redirectTo?: string;
  components?: ExtendedComponentSchema<any>[];
  portalId?: string;
  preloadedFormDataConfig?: PreloadedFormDataConfig;
  validationSchema?: { [key: string]: AnySchema };
  verificationStatusText?: {
    isExpired?: {
      title: string;
      text?: string;
    };
    isInvalid?: {
      title: string;
      text?: string;
    };
    success?: {
      title: string;
      text?: string;
    };
  };
  parameters?: {
    name: string;
    type: string;
  }[];
}

export interface ClientSideWizardStepProps {
  title?: string;
  contentsConfig: ClientSideWizardContentsConfig;
  externalProps?: GenericObject;
  externalEffectCallbacks?: {
    onChange?: (event?: any, name?: string, value?: any) => void;
    onClick?: (event: any) => void;
  };
}

export interface WizardActionsSettings {
  countdown: boolean;
  dataToPreload: string | string[];
}

export interface FormTitleSettings {
  dataToPreload: string | string[];
  dataTransformer?: Function;
}

export interface ClientSideWizardStepConfiguration {
  Component?: any;
  hasStepIndicator?: boolean;
  isFirstStep?: boolean;
  parameters: ClientSideWizardContentsConfig;
  actions?: string[];
  backable?: boolean;
  cancelHidden?: boolean;
  stepId: string | undefined;
  title?: string;
  preloadDataAdapter?: (data: GenericObject) => GenericObject;
  submitDataAdapter?: (data: GenericObject) => GenericObject;
}

export interface ClientSideWizardConfiguration {
  // TODO create layout config type
  currentStep: string | null;
  data: { [key: string]: ClientSideWizardStepConfiguration };
}

export enum WizardNavigationActionsEnum {
  next = 'next',
  prev = 'prev',
}

export enum WizardStepperTypeEnum {
  custom = 'custom',
  generic = 'generic',
}

export enum ClientSideWizardActionTypes {
  Init = '@@ClientSideWizard/INIT',
  GoToNextStep = '@@ClientSideWizard/GO_TO_NEXT_STEP',
  SetStepsConfig = '@@ClientSideWizard/SET_STEPS_CONFIG',
  GoToPreviousStep = '@@ClientSideWizard/GO_TO_PREVIOUDS_STEP',
  SetNavigationAction = '@@ClientSideWizard/SET_NAVIGATE_ACTION',
  SetNetworkCallErrors = '@@ClientSideWizard/SET_NETWORK_CALL_ERRORS',
  SetClientSideWizardState = '@@ClientSideWizard/SET_CLIENT_SIDE_WIZARD_STATE',
}
